// The VFX sprite atlas (§11, §12.1): every particle and ground-decal shape, drawn procedurally with
// Canvas 2D at boot. Each 128 px cell stores four channels so one sprite can be tinted at runtime
// while keeping its comic ink outline: R = fill shade (multiplies the tint), G = ink mask (blends to
// #2A1A3A), B = highlight mask (blends to white), A = coverage. Outside a browser (unit tests) a
// 1 × 1 placeholder texture is used, so the VFX logic runs headless.
import { DataTexture, LinearFilter, LinearMipmapLinearFilter, NoColorSpace, RGBAFormat, type Texture } from 'three';
import { CanvasTexture } from 'three';

/** Sprite cells, row-major in an 8 × 6 grid. */
export const Cell = {
  DOT: 0, PUFF: 1, STAR: 2, SPARK: 3, SPARKLE: 4, RING: 5, DROP: 6, CONFETTI: 7,
  FLAP: 8, CHUNK: 9, HEART: 10, NOTE: 11, BUBBLE: 12, FLOWER: 13, SMOKE: 14, FIRE: 15,
  SHRED: 16, CROWN: 17, SPEEDLINE: 18, SWISH: 19, GLINT: 20, DISCO: 21, SKID: 22, SCORCH: 23,
  SOFTRING: 24, DISC: 25, TARGET: 26, ARC: 27, ARROW: 28, SPLAT: 29, DASH: 30, GLOW: 31,
  BURST: 32, STREAK: 33, CLOUD: 34, FLAME: 35, BURST2: 36, CRACK: 37, WAVE: 38, PEBBLE: 39,
  EMBER: 40, BLOB: 41, SPLASH: 42, IMPACT: 43, PUDDLE: 44,
} as const;
export type Cell = (typeof Cell)[keyof typeof Cell];

export const ATLAS = { cols: 8, rows: 6, cell: 128 } as const;

type Draw = (g: Layers, s: number) => void;

/** The three grayscale layers a cell is drawn into (white on transparent). */
interface Layers { fill: CanvasRenderingContext2D; ink: CanvasRenderingContext2D; hi: CanvasRenderingContext2D }

function star(ctx: CanvasRenderingContext2D, cx: number, cy: number, outer: number, inner: number, points: number, rot = -Math.PI / 2): void {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = rot + (i * Math.PI) / points;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function shapeAll(g: Layers, path: (ctx: CanvasRenderingContext2D) => void, inkW: number, shade = 255): void {
  path(g.fill);
  g.fill.fillStyle = `rgb(${shade},${shade},${shade})`;
  g.fill.fill();
  if (inkW > 0) {
    path(g.ink);
    g.ink.lineWidth = inkW;
    g.ink.lineJoin = 'round';
    g.ink.lineCap = 'round';
    g.ink.strokeStyle = '#fff';
    g.ink.stroke();
  }
}

/** Darkens the lower-right of the current fill with a soft gradient (toon two-tone shading). */
function shadeLower(g: Layers, s: number, amount: number): void {
  const ctx = g.fill;
  ctx.save();
  ctx.globalCompositeOperation = 'source-atop';
  const grad = ctx.createLinearGradient(s * 0.3, s * 0.3, s * 0.75, s * 0.85);
  grad.addColorStop(0, 'rgba(0,0,0,0)');
  grad.addColorStop(0.55, 'rgba(0,0,0,0)');
  grad.addColorStop(0.56, `rgba(0,0,0,${amount})`);
  grad.addColorStop(1, `rgba(0,0,0,${amount})`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, s, s);
  ctx.restore();
}

function blob(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.moveTo(x + r, y);
  ctx.arc(x, y, r, 0, Math.PI * 2);
}

/** A circle (cx, cy, r) in cell fractions. */
type Circle = readonly [number, number, number];

/**
 * A merged silhouette of overlapping circles: the fill is their union and the ink rim follows only
 * the outer edge (each circle's stroke minus the union shrunk by half the rim), so lumpy shapes
 * (puffs, fireballs, clouds) have no inner seams.
 */
function cluster(g: Layers, s: number, circles: readonly Circle[], inkW: number, inkAlpha: number): void {
  const path = (ctx: CanvasRenderingContext2D, grow: number): void => {
    ctx.beginPath();
    for (const [x, y, r] of circles) blob(ctx, x * s, y * s, Math.max(0, r * s + grow));
  };
  path(g.fill, 0);
  g.fill.fillStyle = '#fff';
  g.fill.fill();
  if (inkW <= 0) return;
  const w = inkW * s;
  const ink = g.ink;
  ink.save();
  // Rim on its own: draw the strokes, cut the interior, then fade it by drawing through alpha.
  ink.globalAlpha = inkAlpha;
  path(ink, 0);
  ink.lineWidth = w;
  ink.strokeStyle = '#fff';
  ink.stroke();
  ink.globalCompositeOperation = 'destination-out';
  ink.globalAlpha = 1;
  path(ink, -w / 2);
  ink.fillStyle = '#fff';
  ink.fill();
  if (inkAlpha < 1) {
    // A soft rim stays inside the silhouette (a darker edge), never a see-through halo around it.
    ink.globalCompositeOperation = 'destination-in';
    path(ink, 0);
    ink.fill();
  }
  ink.restore();
}

/** A soft-edged highlight core inside a cluster (blends toward white): hot fireball centers, lit puffs. */
function clusterCore(g: Layers, s: number, circles: readonly Circle[], alpha: number): void {
  const ctx = g.hi;
  ctx.save();
  ctx.filter = `blur(${(s * 0.025).toFixed(1)}px)`;
  ctx.beginPath();
  for (const [x, y, r] of circles) blob(ctx, x * s, y * s, r * s);
  ctx.fillStyle = `rgba(255,255,255,${alpha})`;
  ctx.fill();
  ctx.restore();
}

const DRAW: Readonly<Record<Cell, Draw>> = {
  [Cell.DOT]: (g, s) => {
    const grad = g.fill.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.4, 'rgba(255,255,255,0.75)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fill.fillStyle = grad;
    g.fill.fillRect(0, 0, s, s);
  },
  [Cell.PUFF]: (g, s) => {
    // A soft dust puff: one merged silhouette (no inner seams), a light ink rim and a lit crown.
    const c: Circle[] = [[0.36, 0.58, 0.25], [0.63, 0.55, 0.27], [0.5, 0.37, 0.24], [0.48, 0.66, 0.22]];
    cluster(g, s, c, 0.08, 0.6);
    shadeLower(g, s, 0.2);
    clusterCore(g, s, [[0.44, 0.38, 0.13], [0.34, 0.5, 0.1]], 0.55);
  },
  [Cell.STAR]: (g, s) => {
    shapeAll(g, (ctx) => star(ctx, s / 2, s * 0.53, s * 0.42, s * 0.2, 5), s * 0.07);
    shadeLower(g, s, 0.18);
    g.hi.beginPath();
    blob(g.hi, s * 0.43, s * 0.4, s * 0.06);
    g.hi.fillStyle = '#fff';
    g.hi.fill();
  },
  [Cell.SPARK]: (g, s) => {
    const ctx = g.fill;
    ctx.beginPath();
    ctx.moveTo(s / 2, s * 0.02);
    ctx.quadraticCurveTo(s * 0.6, s * 0.5, s / 2, s * 0.98);
    ctx.quadraticCurveTo(s * 0.4, s * 0.5, s / 2, s * 0.02);
    ctx.fillStyle = '#fff';
    ctx.fill();
  },
  [Cell.SPARKLE]: (g, s) => {
    shapeAll(g, (ctx) => star(ctx, s / 2, s / 2, s * 0.46, s * 0.1, 4), s * 0.05);
    g.hi.beginPath();
    blob(g.hi, s / 2, s / 2, s * 0.08);
    g.hi.fillStyle = '#fff';
    g.hi.fill();
  },
  [Cell.RING]: (g, s) => {
    const path = (ctx: CanvasRenderingContext2D): void => {
      ctx.beginPath();
      ctx.arc(s / 2, s / 2, s * 0.4, 0, Math.PI * 2);
    };
    g.fill.lineWidth = s * 0.1;
    path(g.fill);
    g.fill.strokeStyle = '#fff';
    g.fill.stroke();
    g.ink.lineWidth = s * 0.03;
    g.ink.strokeStyle = '#fff';
    for (const r of [0.35, 0.45]) {
      g.ink.beginPath();
      g.ink.arc(s / 2, s / 2, s * r, 0, Math.PI * 2);
      g.ink.stroke();
    }
  },
  [Cell.DROP]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.moveTo(s / 2, s * 0.08);
      ctx.bezierCurveTo(s * 0.62, s * 0.36, s * 0.78, s * 0.52, s * 0.78, s * 0.66);
      ctx.arc(s / 2, s * 0.66, s * 0.28, 0, Math.PI);
      ctx.bezierCurveTo(s * 0.22, s * 0.52, s * 0.38, s * 0.36, s / 2, s * 0.08);
      ctx.closePath();
    }, s * 0.06);
    shadeLower(g, s, 0.2);
    g.hi.beginPath();
    g.hi.ellipse(s * 0.4, s * 0.62, s * 0.06, s * 0.1, -0.3, 0, Math.PI * 2);
    g.hi.fillStyle = '#fff';
    g.hi.fill();
  },
  [Cell.CONFETTI]: (g, s) => {
    g.fill.beginPath();
    g.fill.roundRect(s * 0.22, s * 0.32, s * 0.56, s * 0.36, s * 0.06);
    g.fill.fillStyle = '#fff';
    g.fill.fill();
  },
  [Cell.FLAP]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.moveTo(s * 0.18, s * 0.22);
      ctx.lineTo(s * 0.84, s * 0.3);
      ctx.lineTo(s * 0.78, s * 0.8);
      ctx.lineTo(s * 0.2, s * 0.74);
      ctx.closePath();
    }, s * 0.06);
    g.ink.beginPath();
    g.ink.moveTo(s * 0.2, s * 0.5);
    g.ink.lineTo(s * 0.8, s * 0.55);
    g.ink.lineWidth = s * 0.025;
    g.ink.globalAlpha = 0.6;
    g.ink.stroke();
    g.ink.globalAlpha = 1;
    shadeLower(g, s, 0.15);
  },
  [Cell.CHUNK]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.moveTo(s * 0.3, s * 0.2);
      ctx.lineTo(s * 0.72, s * 0.26);
      ctx.lineTo(s * 0.82, s * 0.6);
      ctx.lineTo(s * 0.5, s * 0.82);
      ctx.lineTo(s * 0.2, s * 0.62);
      ctx.closePath();
    }, s * 0.07);
    shadeLower(g, s, 0.25);
  },
  [Cell.HEART]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.moveTo(s / 2, s * 0.84);
      ctx.bezierCurveTo(s * 0.1, s * 0.56, s * 0.12, s * 0.18, s * 0.34, s * 0.2);
      ctx.bezierCurveTo(s * 0.44, s * 0.2, s * 0.5, s * 0.3, s / 2, s * 0.34);
      ctx.bezierCurveTo(s * 0.5, s * 0.3, s * 0.56, s * 0.2, s * 0.66, s * 0.2);
      ctx.bezierCurveTo(s * 0.88, s * 0.18, s * 0.9, s * 0.56, s / 2, s * 0.84);
      ctx.closePath();
    }, s * 0.06);
    shadeLower(g, s, 0.18);
    g.hi.beginPath();
    blob(g.hi, s * 0.32, s * 0.34, s * 0.06);
    g.hi.fillStyle = '#fff';
    g.hi.fill();
  },
  [Cell.NOTE]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.ellipse(s * 0.36, s * 0.72, s * 0.16, s * 0.12, -0.4, 0, Math.PI * 2);
      ctx.rect(s * 0.47, s * 0.16, s * 0.07, s * 0.56);
      ctx.moveTo(s * 0.54, s * 0.16);
      ctx.quadraticCurveTo(s * 0.86, s * 0.22, s * 0.78, s * 0.48);
      ctx.quadraticCurveTo(s * 0.74, s * 0.32, s * 0.54, s * 0.32);
      ctx.closePath();
    }, s * 0.05);
  },
  [Cell.BUBBLE]: (g, s) => {
    g.fill.beginPath();
    g.fill.arc(s / 2, s / 2, s * 0.4, 0, Math.PI * 2);
    g.fill.fillStyle = 'rgba(255,255,255,0.25)';
    g.fill.fill();
    g.ink.beginPath();
    g.ink.arc(s / 2, s / 2, s * 0.4, 0, Math.PI * 2);
    g.ink.lineWidth = s * 0.045;
    g.ink.strokeStyle = '#fff';
    g.ink.stroke();
    g.hi.beginPath();
    g.hi.arc(s / 2, s / 2, s * 0.3, Math.PI * 1.05, Math.PI * 1.45);
    g.hi.lineWidth = s * 0.06;
    g.hi.lineCap = 'round';
    g.hi.strokeStyle = '#fff';
    g.hi.stroke();
  },
  [Cell.FLOWER]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        blob(ctx, s / 2 + Math.cos(a) * s * 0.22, s / 2 + Math.sin(a) * s * 0.22, s * 0.17);
      }
    }, s * 0.05);
    g.hi.beginPath();
    blob(g.hi, s / 2, s / 2, s * 0.13);
    g.hi.fillStyle = '#fff';
    g.hi.fill();
    g.ink.beginPath();
    g.ink.arc(s / 2, s / 2, s * 0.13, 0, Math.PI * 2);
    g.ink.lineWidth = s * 0.035;
    g.ink.strokeStyle = '#fff';
    g.ink.stroke();
  },
  [Cell.SMOKE]: (g, s) => {
    for (const [x, y, r] of [[0.4, 0.55, 0.3], [0.62, 0.5, 0.3], [0.5, 0.38, 0.28]] as const) {
      const grad = g.fill.createRadialGradient(s * x, s * y, 0, s * x, s * y, s * r);
      grad.addColorStop(0, 'rgba(255,255,255,0.9)');
      grad.addColorStop(0.7, 'rgba(255,255,255,0.6)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      g.fill.fillStyle = grad;
      g.fill.fillRect(0, 0, s, s);
    }
  },
  [Cell.FIRE]: (g, s) => {
    // A toon fireball: one lumpy silhouette with an ink rim, a big hot core (highlight channel,
    // which blends toward white) and a darker lower edge. No small specular dots (they read as eyes).
    const c: Circle[] = [[0.5, 0.56, 0.3], [0.3, 0.5, 0.19], [0.7, 0.48, 0.2], [0.5, 0.3, 0.21], [0.35, 0.7, 0.16], [0.66, 0.71, 0.16]];
    cluster(g, s, c, 0.05, 1);
    shadeLower(g, s, 0.32);
    clusterCore(g, s, [[0.47, 0.47, 0.17], [0.4, 0.36, 0.12], [0.58, 0.4, 0.1]], 0.92);
  },
  [Cell.SHRED]: (g, s) => {
    const path = (ctx: CanvasRenderingContext2D): void => {
      ctx.beginPath();
      ctx.moveTo(s * 0.2, s * 0.3);
      ctx.lineTo(s * 0.45, s * 0.42);
      ctx.lineTo(s * 0.38, s * 0.56);
      ctx.lineTo(s * 0.8, s * 0.72);
    };
    g.fill.lineWidth = s * 0.14;
    g.fill.lineJoin = 'round';
    g.fill.lineCap = 'round';
    path(g.fill);
    g.fill.strokeStyle = '#fff';
    g.fill.stroke();
    g.ink.lineWidth = s * 0.22;
    g.ink.lineJoin = 'round';
    g.ink.lineCap = 'round';
    path(g.ink);
    g.ink.strokeStyle = '#fff';
    g.ink.stroke();
    g.ink.globalCompositeOperation = 'destination-out';
    g.ink.lineWidth = s * 0.12;
    path(g.ink);
    g.ink.stroke();
    g.ink.globalCompositeOperation = 'source-over';
  },
  [Cell.CROWN]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.moveTo(s * 0.16, s * 0.74);
      ctx.lineTo(s * 0.14, s * 0.3);
      ctx.lineTo(s * 0.34, s * 0.5);
      ctx.lineTo(s * 0.5, s * 0.22);
      ctx.lineTo(s * 0.66, s * 0.5);
      ctx.lineTo(s * 0.86, s * 0.3);
      ctx.lineTo(s * 0.84, s * 0.74);
      ctx.closePath();
    }, s * 0.06);
    shadeLower(g, s, 0.2);
    g.hi.fillStyle = '#fff';
    for (const x of [0.14, 0.5, 0.86]) {
      g.hi.beginPath();
      blob(g.hi, s * x, s * (x === 0.5 ? 0.22 : 0.3), s * 0.05);
      g.hi.fill();
    }
  },
  [Cell.SPEEDLINE]: (g, s) => {
    // A manga speed line: a long sliver, fat in the middle (60% of the cell) and pointed at both ends,
    // so a 0.1 m sprite still reads as a 2–3 px streak from the gameplay camera.
    const ctx = g.fill;
    ctx.beginPath();
    ctx.moveTo(s / 2, s * 0.01);
    ctx.quadraticCurveTo(s * 0.8, s * 0.45, s / 2, s * 0.99);
    ctx.quadraticCurveTo(s * 0.2, s * 0.45, s / 2, s * 0.01);
    ctx.closePath();
    ctx.fillStyle = '#fff';
    ctx.fill();
  },
  [Cell.SWISH]: (g, s) => {
    const path = (ctx: CanvasRenderingContext2D): void => {
      ctx.beginPath();
      ctx.arc(s / 2, s * 0.85, s * 0.6, Math.PI * 1.22, Math.PI * 1.78);
    };
    g.fill.lineWidth = s * 0.09;
    g.fill.lineCap = 'round';
    path(g.fill);
    g.fill.strokeStyle = '#fff';
    g.fill.stroke();
    g.ink.lineWidth = s * 0.15;
    g.ink.lineCap = 'round';
    path(g.ink);
    g.ink.strokeStyle = '#fff';
    g.ink.stroke();
    g.ink.globalCompositeOperation = 'destination-out';
    g.ink.lineWidth = s * 0.08;
    path(g.ink);
    g.ink.stroke();
    g.ink.globalCompositeOperation = 'source-over';
  },
  [Cell.GLINT]: (g, s) => {
    const ctx = g.fill;
    star(ctx, s / 2, s / 2, s * 0.48, s * 0.05, 4, 0);
    ctx.fillStyle = '#fff';
    ctx.fill();
    const grad = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s * 0.25);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, s, s);
  },
  [Cell.DISCO]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.rect(s * 0.26, s * 0.26, s * 0.48, s * 0.48);
    }, s * 0.05);
    g.hi.beginPath();
    g.hi.moveTo(s * 0.3, s * 0.3);
    g.hi.lineTo(s * 0.5, s * 0.3);
    g.hi.lineTo(s * 0.3, s * 0.5);
    g.hi.closePath();
    g.hi.fillStyle = '#fff';
    g.hi.fill();
  },
  [Cell.SKID]: (g, s) => {
    const ctx = g.fill;
    for (const x of [0.36, 0.64]) {
      const grad = ctx.createLinearGradient(0, s * 0.05, 0, s * 0.95);
      grad.addColorStop(0, 'rgba(255,255,255,0)');
      grad.addColorStop(0.3, 'rgba(255,255,255,0.9)');
      grad.addColorStop(1, 'rgba(255,255,255,0.9)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(s * (x - 0.07), s * 0.05, s * 0.14, s * 0.9, s * 0.07);
      ctx.fill();
    }
  },
  [Cell.SCORCH]: (g, s) => {
    const ctx = g.fill;
    ctx.beginPath();
    blob(ctx, s / 2, s / 2, s * 0.3);
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2 + 0.3;
      const r = s * (0.3 + 0.12 * ((i * 7) % 3));
      blob(ctx, s / 2 + Math.cos(a) * r, s / 2 + Math.sin(a) * r, s * (0.05 + 0.03 * (i % 2)));
    }
    ctx.fillStyle = '#fff';
    ctx.fill();
    const grad = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s * 0.32);
    grad.addColorStop(0, 'rgba(0,0,0,0.35)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, s, s);
    ctx.globalCompositeOperation = 'source-over';
  },
  [Cell.SOFTRING]: (g, s) => {
    const grad = g.fill.createRadialGradient(s / 2, s / 2, s * 0.2, s / 2, s / 2, s / 2);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.65, 'rgba(255,255,255,1)');
    grad.addColorStop(0.8, 'rgba(255,255,255,0.6)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fill.fillStyle = grad;
    g.fill.fillRect(0, 0, s, s);
  },
  [Cell.DISC]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.arc(s / 2, s / 2, s * 0.43, 0, Math.PI * 2);
    }, s * 0.05);
  },
  [Cell.TARGET]: (g, s) => {
    const ctx = g.fill;
    ctx.lineWidth = s * 0.07;
    ctx.strokeStyle = '#fff';
    ctx.beginPath();
    ctx.arc(s / 2, s / 2, s * 0.4, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2;
      ctx.moveTo(s / 2 + Math.cos(a) * s * 0.18, s / 2 + Math.sin(a) * s * 0.18);
      ctx.lineTo(s / 2 + Math.cos(a) * s * 0.3, s / 2 + Math.sin(a) * s * 0.3);
    }
    ctx.stroke();
    g.ink.lineWidth = s * 0.025;
    g.ink.strokeStyle = '#fff';
    for (const r of [0.355, 0.445]) {
      g.ink.beginPath();
      g.ink.arc(s / 2, s / 2, s * r, 0, Math.PI * 2);
      g.ink.stroke();
    }
  },
  [Cell.ARC]: (g, s) => {
    // A 60° sector pointing up (+V), apex at the bottom center: the glove punch telegraph.
    const path = (ctx: CanvasRenderingContext2D): void => {
      ctx.beginPath();
      ctx.moveTo(s / 2, s * 0.96);
      ctx.arc(s / 2, s * 0.96, s * 0.9, -Math.PI / 2 - Math.PI / 6, -Math.PI / 2 + Math.PI / 6);
      ctx.closePath();
    };
    const grad = g.fill.createLinearGradient(0, s, 0, 0);
    grad.addColorStop(0, 'rgba(255,255,255,0.25)');
    grad.addColorStop(1, 'rgba(255,255,255,0.85)');
    path(g.fill);
    g.fill.fillStyle = grad;
    g.fill.fill();
    path(g.ink);
    g.ink.lineWidth = s * 0.04;
    g.ink.lineJoin = 'round';
    g.ink.strokeStyle = '#fff';
    g.ink.stroke();
  },
  [Cell.ARROW]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.moveTo(s / 2, s * 0.08);
      ctx.lineTo(s * 0.9, s * 0.62);
      ctx.lineTo(s * 0.64, s * 0.6);
      ctx.lineTo(s * 0.64, s * 0.92);
      ctx.lineTo(s * 0.36, s * 0.92);
      ctx.lineTo(s * 0.36, s * 0.6);
      ctx.lineTo(s * 0.1, s * 0.62);
      ctx.closePath();
    }, s * 0.06);
  },
  [Cell.SPLAT]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      blob(ctx, s / 2, s / 2, s * 0.24);
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const r = s * (0.26 + 0.08 * (i % 3));
        blob(ctx, s / 2 + Math.cos(a) * r, s / 2 + Math.sin(a) * r, s * (0.06 + 0.02 * (i % 2)));
      }
    }, s * 0.045);
    shadeLower(g, s, 0.15);
  },
  [Cell.DASH]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.roundRect(s * 0.38, s * 0.1, s * 0.24, s * 0.8, s * 0.12);
    }, s * 0.05);
  },
  [Cell.GLOW]: (g, s) => {
    const grad = g.fill.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grad.addColorStop(0, 'rgba(255,255,255,0.9)');
    grad.addColorStop(0.35, 'rgba(255,255,255,0.45)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fill.fillStyle = grad;
    g.fill.fillRect(0, 0, s, s);
  },
  [Cell.BURST]: (g, s) => {
    // The comic impact burst: a jagged 12-point star with an inner hot core.
    const jag = [0.47, 0.27, 0.43, 0.25, 0.49, 0.29, 0.41, 0.24, 0.46, 0.28, 0.44, 0.26, 0.48, 0.25, 0.42, 0.29, 0.47, 0.24, 0.44, 0.27, 0.49, 0.26, 0.42, 0.28];
    const path = (ctx: CanvasRenderingContext2D, k: number): void => {
      ctx.beginPath();
      for (let i = 0; i < jag.length; i++) {
        const a = -Math.PI / 2 + (i * Math.PI * 2) / jag.length;
        const r = s * jag[i] * k;
        if (i === 0) ctx.moveTo(s / 2 + Math.cos(a) * r, s / 2 + Math.sin(a) * r);
        else ctx.lineTo(s / 2 + Math.cos(a) * r, s / 2 + Math.sin(a) * r);
      }
      ctx.closePath();
    };
    shapeAll(g, (ctx) => path(ctx, 1), s * 0.05);
    path(g.hi, 0.55);
    g.hi.fillStyle = '#fff';
    g.hi.fill();
  },
  [Cell.STREAK]: (g, s) => {
    // An action line: a long tapered wedge with an ink edge, pointing up.
    const path = (ctx: CanvasRenderingContext2D): void => {
      ctx.beginPath();
      ctx.moveTo(s / 2, s * 0.02);
      ctx.lineTo(s * 0.58, s * 0.9);
      ctx.quadraticCurveTo(s / 2, s * 0.98, s * 0.42, s * 0.9);
      ctx.closePath();
    };
    shapeAll(g, path, s * 0.035);
  },
  [Cell.CLOUD]: (g, s) => {
    // A toon smoke cloud: one lumpy silhouette, flat-shaded, a soft (60%) ink rim, a lit crown.
    const c: Circle[] = [[0.3, 0.6, 0.19], [0.5, 0.64, 0.2], [0.7, 0.58, 0.18], [0.42, 0.42, 0.21], [0.62, 0.4, 0.17]];
    cluster(g, s, c, 0.07, 0.6);
    shadeLower(g, s, 0.22);
    clusterCore(g, s, [[0.42, 0.36, 0.1], [0.6, 0.34, 0.07]], 0.5);
  },
  [Cell.FLAME]: (g, s) => {
    // A flame tongue pointing up with a hot inner core (highlight channel).
    const path = (ctx: CanvasRenderingContext2D, k: number): void => {
      ctx.beginPath();
      ctx.moveTo(s / 2, s * (0.5 - 0.46 * k));
      ctx.bezierCurveTo(s * (0.5 + 0.12 * k), s * (0.5 - 0.2 * k), s * (0.5 + 0.36 * k), s * (0.5 + 0.02 * k), s * (0.5 + 0.3 * k), s * (0.5 + 0.26 * k));
      ctx.bezierCurveTo(s * (0.5 + 0.24 * k), s * (0.5 + 0.44 * k), s * (0.5 - 0.24 * k), s * (0.5 + 0.44 * k), s * (0.5 - 0.3 * k), s * (0.5 + 0.26 * k));
      ctx.bezierCurveTo(s * (0.5 - 0.36 * k), s * (0.5 + 0.02 * k), s * (0.5 - 0.12 * k), s * (0.5 - 0.2 * k), s / 2, s * (0.5 - 0.46 * k));
      ctx.closePath();
    };
    shapeAll(g, (ctx) => path(ctx, 1), s * 0.05);
    path(g.hi, 0.55);
    g.hi.fillStyle = 'rgba(255,255,255,0.95)';
    g.hi.fill();
  },
  [Cell.BURST2]: (g, s) => {
    // A softer 8-point "kapow" burst (clashes, crate pops).
    shapeAll(g, (ctx) => star(ctx, s / 2, s / 2, s * 0.47, s * 0.3, 8, -Math.PI / 8), s * 0.05);
    g.hi.beginPath();
    star(g.hi, s / 2, s / 2, s * 0.24, s * 0.15, 8, -Math.PI / 8);
    g.hi.fillStyle = '#fff';
    g.hi.fill();
  },
  [Cell.CRACK]: (g, s) => {
    // Ground cracks radiating from the center (stomps, heavy landings).
    const ctx = g.ink;
    ctx.strokeStyle = '#fff';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const arms = 7;
    for (let i = 0; i < arms; i++) {
      const a = (i / arms) * Math.PI * 2 + 0.3 * Math.sin(i * 2.1);
      let x = s / 2;
      let y = s / 2;
      ctx.lineWidth = s * 0.035;
      ctx.beginPath();
      ctx.moveTo(x, y);
      for (let k = 1; k <= 3; k++) {
        const r = s * 0.14 * k;
        const w = 0.35 * Math.sin(i * 3.7 + k * 1.9);
        x = s / 2 + Math.cos(a + w) * r;
        y = s / 2 + Math.sin(a + w) * r;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    g.fill.beginPath();
    blob(g.fill, s / 2, s / 2, s * 0.12);
    g.fill.fillStyle = 'rgba(255,255,255,0.6)';
    g.fill.fill();
  },
  [Cell.WAVE]: (g, s) => {
    // A ground shockwave: a crisp bright band between two ink lines (reads on light and dark floors)
    // with a faint trail inside, so the expanding ring never looks like a filled bubble.
    const c = s / 2;
    const grad = g.fill.createRadialGradient(c, c, s * 0.28, c, c, s * 0.4);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(1, 'rgba(255,255,255,0.22)');
    g.fill.fillStyle = grad;
    g.fill.beginPath();
    g.fill.arc(c, c, s * 0.4, 0, Math.PI * 2);
    g.fill.fill();
    g.fill.beginPath();
    g.fill.arc(c, c, s * 0.432, 0, Math.PI * 2);
    g.fill.lineWidth = s * 0.06;
    g.fill.strokeStyle = '#fff';
    g.fill.stroke();
    g.ink.strokeStyle = '#fff';
    g.ink.lineWidth = s * 0.016;
    g.ink.globalAlpha = 0.85;
    g.ink.beginPath();
    g.ink.arc(c, c, s * 0.468, 0, Math.PI * 2);
    g.ink.stroke();
    g.ink.globalAlpha = 0.45;
    g.ink.beginPath();
    g.ink.arc(c, c, s * 0.396, 0, Math.PI * 2);
    g.ink.stroke();
    g.ink.globalAlpha = 1;
  },
  [Cell.EMBER]: (g, s) => {
    // The cooler outer fireball puff: the FIRE silhouette with a bold ink rim and a soft warm lit
    // crown, no white-hot core (stacked under hotter puffs, the ball reads hot at the center only).
    const c: Circle[] = [[0.5, 0.56, 0.3], [0.3, 0.5, 0.19], [0.7, 0.48, 0.2], [0.5, 0.3, 0.21], [0.35, 0.7, 0.16], [0.66, 0.71, 0.16]];
    cluster(g, s, c, 0.055, 1);
    shadeLower(g, s, 0.3);
    clusterCore(g, s, [[0.42, 0.36, 0.1]], 0.35);
  },
  [Cell.BLOB]: (g, s) => {
    // A round liquid blob (splash crowns and columns): ink rim, a darker lower half, a glossy streak.
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.arc(s / 2, s / 2, s * 0.4, 0, Math.PI * 2);
    }, s * 0.06);
    shadeLower(g, s, 0.25);
    g.hi.save();
    g.hi.translate(s * 0.38, s * 0.36);
    g.hi.rotate(-0.7);
    g.hi.beginPath();
    g.hi.ellipse(0, 0, s * 0.11, s * 0.06, 0, 0, Math.PI * 2);
    g.hi.fillStyle = 'rgba(255,255,255,0.95)';
    g.hi.fill();
    g.hi.restore();
  },
  [Cell.SPLASH]: (g, s) => {
    // A splash crown sheet seen from the side: a low cup of liquid whose rim bursts into fat,
    // uneven tongues leaning outward, plus drops flung free above (§11.2 KO splash). Drawn as a union
    // of simple shapes with one outer ink line.
    const shapes = (ctx: CanvasRenderingContext2D): void => {
      ctx.beginPath();
      ctx.ellipse(s * 0.5, s * 0.8, s * 0.4, s * 0.13, 0, 0, Math.PI * 2);
      ctx.moveTo(s * 0.14, s * 0.8);
      ctx.lineTo(s * 0.2, s * 0.58);
      ctx.lineTo(s * 0.8, s * 0.58);
      ctx.lineTo(s * 0.86, s * 0.8);
      ctx.closePath();
      for (const [x, y, rx, ry, rot] of [
        [0.17, 0.6, 0.065, 0.13, -0.45], [0.31, 0.49, 0.07, 0.17, -0.22], [0.46, 0.46, 0.075, 0.2, -0.05],
        [0.6, 0.41, 0.075, 0.23, 0.08], [0.73, 0.51, 0.07, 0.16, 0.28], [0.84, 0.62, 0.06, 0.11, 0.5],
      ] as const) {
        ctx.moveTo(s * (x + rx), s * y);
        ctx.ellipse(s * x, s * y, s * rx, s * ry, rot, 0, Math.PI * 2);
      }
      for (const [x, y, r] of [[0.2, 0.14, 0.045], [0.4, 0.07, 0.045], [0.78, 0.17, 0.04], [0.62, 0.06, 0.035]] as const) blob(ctx, x * s, y * s, r * s);
    };
    shapes(g.fill);
    g.fill.fillStyle = '#fff';
    g.fill.fill('nonzero');
    const w = s * 0.07;
    const ink = g.ink;
    ink.save();
    shapes(ink);
    ink.lineWidth = w;
    ink.lineJoin = 'round';
    ink.strokeStyle = '#fff';
    ink.stroke();
    ink.globalCompositeOperation = 'destination-out';
    shapes(ink);
    ink.fillStyle = '#fff';
    ink.fill('nonzero');
    ink.restore();
    // The inside of the cup is darker; a glossy streak down the near wall.
    const ctx = g.fill;
    ctx.save();
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.beginPath();
    ctx.ellipse(s * 0.5, s * 0.66, s * 0.3, s * 0.07, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    g.hi.beginPath();
    g.hi.ellipse(s * 0.27, s * 0.74, s * 0.035, s * 0.08, 0.25, 0, Math.PI * 2);
    g.hi.ellipse(s * 0.46, s * 0.42, s * 0.025, s * 0.08, -0.05, 0, Math.PI * 2);
    g.hi.fillStyle = 'rgba(255,255,255,0.8)';
    g.hi.fill();
  },
  [Cell.IMPACT]: (g, s) => {
    // The hit "impact frame": a big jagged white burst with a thick ink rim and a yellow-hot heart,
    // shown for a few frames at the contact so a hit lands with a visible SMACK.
    shapeAll(g, (ctx) => star(ctx, s / 2, s / 2, s * 0.48, s * 0.27, 11, -Math.PI / 2 + 0.13), s * 0.06);
    g.hi.beginPath();
    star(g.hi, s / 2, s / 2, s * 0.3, s * 0.17, 11, -Math.PI / 2 + 0.13);
    g.hi.fillStyle = '#fff';
    g.hi.fill();
  },
  [Cell.PUDDLE]: (g, s) => {
    // The ledge-cooldown "sweaty hands" ground cue: a glossy sweat puddle (a wobbly blob with two
    // highlight glints) and a few satellite droplets, so it reads as wet/slippery, never as a ring.
    const c = s / 2;
    const wob = (a: number): number => s * 0.34 * (1 + 0.09 * Math.sin(3 * a + 0.5) + 0.06 * Math.sin(5 * a + 1.3));
    const puddle = (ctx: CanvasRenderingContext2D): void => {
      ctx.beginPath();
      for (let i = 0; i <= 48; i++) {
        const a = (i / 48) * Math.PI * 2;
        const r = wob(a);
        if (i === 0) ctx.moveTo(c + Math.cos(a) * r, c + Math.sin(a) * r);
        else ctx.lineTo(c + Math.cos(a) * r, c + Math.sin(a) * r);
      }
      ctx.closePath();
      for (const [a, k, rr] of [[0.6, 1.28, 0.045], [2.4, 1.32, 0.035], [4.1, 1.26, 0.05], [5.3, 1.35, 0.03]] as const) {
        blob(ctx, c + Math.cos(a) * wob(a) * k, c + Math.sin(a) * wob(a) * k, s * rr);
      }
    };
    shapeAll(g, puddle, s * 0.028);
    shadeLower(g, s, 0.12);
    g.hi.fillStyle = '#fff';
    g.hi.beginPath();
    g.hi.ellipse(c - s * 0.12, c - s * 0.12, s * 0.1, s * 0.035, -0.6, 0, Math.PI * 2);
    g.hi.fill();
    g.hi.beginPath();
    g.hi.ellipse(c + s * 0.03, c - s * 0.2, s * 0.035, s * 0.018, -0.4, 0, Math.PI * 2);
    g.hi.fill();
  },
  [Cell.PEBBLE]: (g, s) => {
    shapeAll(g, (ctx) => {
      ctx.beginPath();
      ctx.ellipse(s / 2, s / 2, s * 0.36, s * 0.28, 0.2, 0, Math.PI * 2);
    }, s * 0.06);
    shadeLower(g, s, 0.3);
    g.hi.beginPath();
    blob(g.hi, s * 0.4, s * 0.4, s * 0.06);
    g.hi.fillStyle = '#fff';
    g.hi.fill();
  },
};

function layerCanvas(w: number, h: number): CanvasRenderingContext2D {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('VFX atlas: Canvas 2D unavailable');
  return ctx;
}

/** Packs the three layers into the RGBA channel encoding described at the top of the file. */
function combine(fill: ImageData, ink: ImageData, hi: ImageData, out: ImageData): void {
  const f = fill.data;
  const k = ink.data;
  const h = hi.data;
  const o = out.data;
  for (let i = 0; i < o.length; i += 4) {
    const fa = f[i + 3];
    const ka = k[i + 3];
    o[i] = fa > 0 ? f[i] : 255;
    o[i + 1] = ka;
    o[i + 2] = fa > 0 ? Math.round((h[i + 3] * fa) / 255) : 0;
    o[i + 3] = Math.max(fa, ka);
  }
}

function placeholder(): Texture {
  const t = new DataTexture(new Uint8Array([255, 0, 0, 255]), 1, 1, RGBAFormat);
  t.needsUpdate = true;
  return t;
}

let shared: Texture | null = null;

/** The atlas texture (drawn once and shared by every VFX pool). */
export function spriteAtlas(): Texture {
  if (shared) return shared;
  if (typeof document === 'undefined') {
    shared = placeholder();
    return shared;
  }
  const { cols, rows, cell } = ATLAS;
  const w = cols * cell;
  const h = rows * cell;
  const layers: Layers = { fill: layerCanvas(w, h), ink: layerCanvas(w, h), hi: layerCanvas(w, h) };
  const pad = 2;
  for (const key of Object.keys(DRAW)) {
    const id = Number(key) as Cell;
    const x = (id % cols) * cell;
    const y = Math.floor(id / cols) * cell;
    for (const ctx of [layers.fill, layers.ink, layers.hi]) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(x + pad, y + pad, cell - 2 * pad, cell - 2 * pad);
      ctx.clip();
      ctx.translate(x + pad, y + pad);
      const k = (cell - 2 * pad) / cell;
      ctx.scale(k, k);
    }
    DRAW[id](layers, cell);
    for (const ctx of [layers.fill, layers.ink, layers.hi]) ctx.restore();
  }
  const out = layers.fill.createImageData(w, h);
  combine(layers.fill.getImageData(0, 0, w, h), layers.ink.getImageData(0, 0, w, h), layers.hi.getImageData(0, 0, w, h), out);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('VFX atlas: Canvas 2D unavailable');
  ctx.putImageData(out, 0, 0);
  const tex = new CanvasTexture(canvas);
  // The channels are masks, not colors: no sRGB decode, no premultiplication.
  tex.colorSpace = NoColorSpace;
  tex.premultiplyAlpha = false;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 2;
  tex.needsUpdate = true;
  shared = tex;
  return tex;
}

/** UV rect of a cell: [u0, v0, du, dv] with v up (three's flipY on canvas textures). */
export function cellUv(cell: Cell, out: Float32Array, offset: number): void {
  const col = cell % ATLAS.cols;
  const row = Math.floor(cell / ATLAS.cols);
  out[offset] = col / ATLAS.cols;
  out[offset + 1] = 1 - (row + 1) / ATLAS.rows;
  out[offset + 2] = 1 / ATLAS.cols;
  out[offset + 3] = 1 / ATLAS.rows;
}
